import { store } from '../../../../app/store';
import { extractErrorMessage } from '../../../../utils/errorUtils';
import { wiseApi } from '../../api/wiseApi';
import type { CommandHandler } from '../types';
import {
  CONFIRMATION_UNAVAILABLE,
  findInvoiceRow,
  getGridRows,
  getSelectedInvoiceIds,
  invoiceLabel,
} from './wiseInvoiceRows';

const NOTHING_MARKED = 'None of the selected invoices is marked as sent to Wise.';
const ALREADY_PAID =
  'The selected invoices are already paid in Wise; their Wise mark cannot be removed.';

/** Removes the "sent to Wise" mark from the selected invoices; the transfers in Wise are not touched. */
export const unmarkWiseCommandHandler: CommandHandler = async (_command, context) => {
  const invoiceIds = getSelectedInvoiceIds(context);
  if (invoiceIds.length === 0) {
    return { success: false, error: 'Please select at least one invoice.' };
  }

  if (!context.requestConfirmation) {
    return { success: false, error: CONFIRMATION_UNAVAILABLE };
  }

  // Every selected id goes to the server, which skips invoices without a mark or already paid in Wise;
  // the grid may hold stale rows or only the current page, so it is not used to decide.
  const rows = getGridRows(context);
  const numbers = invoiceIds.map((id) => invoiceLabel(findInvoiceRow(rows, id), id)).join(', ');
  const confirmed = await context.requestConfirmation(
    `Remove the Wise mark from ${invoiceIds.length} invoice(s): ${numbers}? ` +
      'Invoices without a mark or already paid in Wise are skipped. ' +
      'The transfers stay in Wise; cancel them there if they must not be paid.'
  );
  if (!confirmed) {
    return { success: false };
  }

  context.setLoading?.(true);
  try {
    const result = await store
      .dispatch(wiseApi.endpoints.unmarkInvoicesInWise.initiate(invoiceIds))
      .unwrap();

    const paidCount = result.PaidCount ?? 0;
    if (result.ClearedCount === 0) {
      return { success: false, error: paidCount > 0 ? ALREADY_PAID : NOTHING_MARKED };
    }

    let message = `Removed the Wise mark from ${result.ClearedCount} invoice(s).`;
    if (result.SkippedCount > 0) {
      message += ` ${result.SkippedCount} invoice(s) had no mark.`;
    }
    if (paidCount > 0) {
      message += ` ${paidCount} invoice(s) are already paid in Wise and keep the mark.`;
    }

    return { success: true, shouldRefresh: true, message };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to remove the Wise mark.'),
    };
  } finally {
    context.setLoading?.(false);
  }
};
